import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import LoadingSpinner from '../loadSpinner/LoadingSpinner.jsx';
import { requestGetManagers } from '../../services/wisdomServices/managerService.js';
import './AddProjectModal.css';
import { Can } from '../../security/Can.jsx';
import { PERMISSIONS } from '../../security/permissions.generated.js';

const getTimezoneOffset = (timeZone) => {
  const now = new Date();

  const utcDate = new Date(
    now.toLocaleString('en-US', { timeZone: 'UTC' })
  );
  const timezoneDate = new Date(
    now.toLocaleString('en-US', { timeZone })
  );

  const diffMinutes = (timezoneDate - utcDate) / (1000 * 60);
  const sign = diffMinutes >= 0 ? '+' : '-';
  const absoluteMinutes = Math.abs(diffMinutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, '0');
  const minutes = String(absoluteMinutes % 60).padStart(2, '0');

  return `${timeZone} ${sign}${hours}:${minutes}`;
};

const TIMEZONE_OPTIONS = [
  { label: 'UTC', value: "UTC +00:00" },
  ...Intl.supportedValuesOf('timeZone').map((timeZone) => ({
    label: timeZone,
    value: getTimezoneOffset(timeZone),
  })),
];

/**
 * @function AddProjectsModal
 * @param {Object} props - Component props.
 * @param {boolean} props.isAddingProject - Indicates whether the project is being created.
 * @param {Function} props.addProject - Callback used to submit the new project.
 * @param {Function} props.onClose - Callback used to close the dialog.
 * @returns {JSX.Element}
 */
function AddProjectsModal({ isAddingProject, addProject, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    contactName: '',
    hours: 0,
    manager: null,
    startTime: null,
    endTime: null,
    industry: "",
    timezone: "UTC +00:00",
    ticketingSystemUrl: "",
    knowledgeBaseUrl: "",
    internal: false,
    isSharedService: false,
  });
  const [managers, setManagers] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  useEffect(() => {
    const getManagers = async () => {
      try {
        setLoadingManagers(true);
        const response = await requestGetManagers(1, 100);
        const managerList = response.data.map((item) => ({
          id: item.id,
          name: `${item.firstName} ${item.lastName}`,
          email: item.username,
        }));
        setManagers(managerList);
      } catch (error) {
        console.error('Error loading managers:', error);
      } finally {
        setLoadingManagers(false);
      }
    };

    getManagers();
  }, []);

  const updateField = (field, value) => {
    setFormData((previous) => ({ ...previous, [field]: value }));
  };

  const formatTimeToHHMM = (date) => {
    if (!(date instanceof Date)) return '';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const isFormValid = () =>
    Boolean(
      formData.name.trim() &&
        formData.contactName.trim() &&
        formData.manager?.id &&
        formData.startTime &&
        formData.endTime &&
        formData.industry.trim() &&
        formData.timezone.trim() &&
        formData.ticketingSystemUrl.trim() &&
        formData.knowledgeBaseUrl.trim() &&
        typeof formData.internal === 'boolean'
    );

  const handleAddProject = (event) => {
    event.preventDefault();
    if (!isFormValid() || isAddingProject) return;

    const project = {
      name: formData.name.trim(),
      contactName: formData.contactName.trim(),
      hours: formData.hours,
      isSharedService: formData.isSharedService,
      managerEmail: formData.manager.email,
      manager: formData.manager.name,
      managerId: formData.manager.id,
      startTime: formatTimeToHHMM(formData.startTime),
      endTime: formatTimeToHHMM(formData.endTime),
      industry: formData.industry.trim(),
      timezone: formData.timezone.trim(),
      ticketingSystemUrl: formData.ticketingSystemUrl.trim(),
      knowledgeBaseUrl: formData.knowledgeBaseUrl.trim(),
      internal: formData.internal,
    };

    addProject(project);
  };

  const dialogHeader = (
    <div className="add-project-dialog-header">
      <span className="add-project-dialog-eyebrow">Project management</span>
      <span className="add-project-dialog-title">Create a new project</span>
    </div>
  );

  const dialogFooter = (
    <div className="add-project-dialog-footer">
      <Message severity="info" text="All (*) fields are mandatory" />

      <div className="add-project-dialog-footer-actions">
        <Button
          label="Cancel"
          icon="pi pi-times"
          severity="secondary"
          outlined
          type="button"
          onClick={onClose}
          disabled={isAddingProject}
        />
        <Can permission={PERMISSIONS.projects_post}>
          <Button
            label="Create project"
            icon="pi pi-check"
            type="submit"
            form="add-project-form"
            disabled={!isFormValid() || isAddingProject}
            loading={isAddingProject}
          />
        </Can>
      </div>
    </div>
  );

  return (
    <Dialog
      className="add-project-dialog"
      contentClassName="add-project-dialog-content"
      visible
      onHide={onClose}
      header={dialogHeader}
      footer={dialogFooter}
      modal
      draggable={false}
      closable={!isAddingProject}
      dismissableMask={!isAddingProject}
      closeOnEscape={!isAddingProject}
      style={{ width: 'min(1050px, 94vw)' }}
    >
      <form
        id="add-project-form"
        className="add-project-form"
        onSubmit={handleAddProject}
      >
        <section className="add-project-section">
          <div className="add-project-section-heading">
            <i className="pi pi-briefcase" />
            <div>
              <h3>General information</h3>
              <p>Project identification, ownership and capacity.</p>
            </div>
          </div>

          <div className="add-project-grid add-project-grid--four">
            <div className="add-project-field add-project-field--wide">
              <label htmlFor="add-project-name">Project name *</label>
              <InputText
                id="add-project-name"
                value={formData.name}
                onChange={(event) => updateField('name', event.target.value)}
                autoFocus
              />
            </div>

            <div className="add-project-field">
              <label htmlFor="add-project-contact-name">Contact name *</label>
              <InputText
                id="add-project-contact-name"
                value={formData.contactName}
                onChange={(event) =>
                  updateField('contactName', event.target.value)
                }
              />
            </div>

            <div className="add-project-field">
              <label htmlFor="add-project-manager">Manager *</label>
              <Dropdown
                inputId="add-project-manager"
                value={formData.manager}
                onChange={(event) => updateField('manager', event.value)}
                options={managers}
                optionLabel="name"
                placeholder="Select a manager"
                loading={loadingManagers}
                filter
              />
            </div>

            <div className="add-project-field">
              <label htmlFor="add-project-industry">Industry *</label>
              <InputText
                id="add-project-industry"
                value={formData.industry}
                placeholder="e.g., Technology, Healthcare, Finance"
                onChange={(event) =>
                  updateField('industry', event.target.value)
                }
              />
            </div>

            <div className="add-project-field">
              <label htmlFor="add-project-hour-cap">Hour cap per agent *</label>
              <InputNumber
                id="add-project-hour-cap"
                min={0}
                useGrouping={false}
                value={formData.hours}
                onValueChange={(event) => updateField('hours', event.value)}
              />
            </div>

            <div className="add-project-field">
              <label htmlFor="add-project-timezone">Timezone *</label>
              <Dropdown
                inputId="add-project-timezone"
                options={TIMEZONE_OPTIONS}
                optionLabel="label"
                optionValue="value"
                value={formData.timezone}
                onChange={(event) => updateField('timezone', event.value)}
                filter
                filterBy="label"
                placeholder="Select a timezone"
              />
            </div>
          </div>
        </section>

        <section className="add-project-section">
          <div className="add-project-section-heading">
            <i className="pi pi-link" />
            <div>
              <h3>Project resources</h3>
              <p>Links used by the support and operations teams.</p>
            </div>
          </div>

          <div className="add-project-grid add-project-grid--two">
            <div className="add-project-field">
              <label htmlFor="add-project-ticketing-url">
                Ticketing system URL *
              </label>
              <span className="p-input-icon-left">
                <i className="pi pi-ticket" />
                <InputText
                  id="add-project-ticketing-url"
                  type="url"
                  value={formData.ticketingSystemUrl}
                  placeholder='https://quinndesk-url-placeholder.com'
                  onChange={(event) =>
                    updateField('ticketingSystemUrl', event.target.value)
                  }
                />
              </span>
            </div>

            <div className="add-project-field">
              <label htmlFor="add-project-knowledge-base-url">
                Knowledge base URL *
              </label>
              <span className="p-input-icon-left">
                <i className="pi pi-book" />
                <InputText
                  id="add-project-knowledge-base-url"
                  type="url"
                  value={formData.knowledgeBaseUrl}
                  placeholder='https://quinndesk-url-placeholder.com'
                  onChange={(event) =>
                    updateField('knowledgeBaseUrl', event.target.value)
                  }
                />
              </span>
            </div>
          </div>
        </section>

        <div className="add-project-section-row">
          <section className="add-project-section">
            <div className="add-project-section-heading">
              <i className="pi pi-clock" />
              <div>
                <h3>Coverage</h3>
                <p>Daily operating window in the selected timezone.</p>
              </div>
            </div>

            <div className="add-project-grid add-project-grid--two">
              <div className="add-project-field">
                <label htmlFor="add-project-start-time">Start *</label>
                <Calendar
                  inputId="add-project-start-time"
                  value={formData.startTime}
                  onChange={(event) => updateField('startTime', event.value)}
                  timeOnly
                  hourFormat="24"
                  showIcon
                />
              </div>

              <div className="add-project-field">
                <label htmlFor="add-project-end-time">End *</label>
                <Calendar
                  inputId="add-project-end-time"
                  minDate={
                    formData.startTime
                      ? new Date(formData.startTime.getTime() + 30 * 60000)
                      : null
                  }
                  value={formData.endTime}
                  onChange={(event) => updateField('endTime', event.value)}
                  timeOnly
                  hourFormat="24"
                  showIcon
                />
              </div>
            </div>
          </section>

          <section className="add-project-section">
            <div className="add-project-section-heading">
              <i className="pi pi-cog" />
              <div>
                <h3>Project settings</h3>
                <p>Service and access options.</p>
              </div>
            </div>

            <div className="add-project-switch-grid">
              <div className="add-project-switch-card">
                <div>
                  <label htmlFor="add-project-shared">Shared service</label>
                  <small>Allow shared service usage.</small>
                </div>
                <InputSwitch
                  inputId="add-project-shared"
                  checked={formData.isSharedService}
                  onChange={(event) =>
                    updateField('isSharedService', event.value)
                  }
                />
              </div>

              <div className="add-project-switch-card">
                <div>
                  <label htmlFor="add-project-internal">Internal *</label>
                  <small>Mark this as an internal project.</small>
                </div>
                <InputSwitch
                  inputId="add-project-internal"
                  checked={formData.internal}
                  onChange={(event) => updateField('internal', event.value)}
                />
              </div>
            </div>
          </section>
        </div>
      </form>

      {isAddingProject && (
        <div className="add-project-loading-overlay">
          <LoadingSpinner />
        </div>
      )}
    </Dialog>
  );
}

export default AddProjectsModal;